import { t } from "@lingui/macro";
import {
  IPageConfigSetQuery,
  IPageConfigSetQueryVariables,
  useInfinitePageConfigSetQuery,
} from "@smp/graphql";
import { dataArrayToHashMap, SetQueryElement } from "@smp/utils";
import produce from "immer";
import { WritableDraft } from "immer/dist/types/types-external";
import { useCallback, useMemo } from "react";
import { UseInfiniteQueryOptions, useQueryClient } from "react-query";
import { UseInfiniteQueryResult } from "react-query/types/react/types";
import { useUpdateEffect } from "react-use";

import { gqlClient } from "@/hooks/use_graphql";

type IEdge = SetQueryElement<IPageConfigSetQuery["page_config_set"]>;

export const useQueryGetPageConfigSetAll = (
  variables?: Parameters<typeof useInfinitePageConfigSetQuery>[2],
  options?: Omit<
    UseInfiniteQueryOptions<IPageConfigSetQuery>,
    "queryFn" | "queryKey" | "getNextPageParam"
  >,
) => {
  const resp = useInfinitePageConfigSetQuery(
    "cursor",
    gqlClient,
    {
      page_size: 100,
      ...variables,
    },
    {
      getNextPageParam: (currentPage) => {
        const { cursor_next } = currentPage.page_config_set.page_info;
        return cursor_next ? { cursor: cursor_next } : undefined;
      },
      ...options,
    },
  );

  const isFetched = useMemo(
    () => resp.isFetched && !resp.hasNextPage,
    [resp.hasNextPage, resp.isFetched],
  );

  const dataArray = useMemo(
    () =>
      isFetched
        ? resp.data?.pages.reduce((pageConfigs, page) => {
            const nextPageConfigs = page.page_config_set.edges.flatMap(
              (edge) => edge.node,
            );
            pageConfigs = [...pageConfigs, ...nextPageConfigs];
            return pageConfigs;
          }, [] as SetQueryElement<IPageConfigSetQuery["page_config_set"]>[])
        : undefined,
    [isFetched, resp.data?.pages],
  );

  const dataNormalized = useMemo(
    () => dataArray && dataArrayToHashMap(dataArray, "id"),
    [dataArray],
  );

  const dataGroupedMap = useMemo(
    () =>
      dataArray?.reduce(
        (acc, edge) => {
          const group = {
            id: edge.group?.id || 0,
            name: edge.group?.name || t`Unassigned`,
          };

          acc.set(group.id, {
            ...group,
            page_configs: [...(acc.get(group.id)?.page_configs || []), edge],
          });

          return acc;
        },
        new Map() as Map<
          NonNullable<IEdge["group"]>["id"],
          IEdge["group"] & {
            page_configs: IEdge[];
          }
        >,
      ),
    [dataArray],
  );

  const dataGrouped = useMemo(
    () =>
      dataGroupedMap &&
      Array.from(dataGroupedMap.values()).sort((g1, g2) => g2.id - g1.id),
    [dataGroupedMap],
  );

  useUpdateEffect(() => {
    if (!resp.isFetching && resp.hasNextPage) {
      resp.fetchNextPage();
    }
  }, [resp.isFetching, resp.hasNextPage]);

  return {
    ...resp,
    data: {
      array: dataArray,
      grouped: dataGrouped,
      normalized: dataNormalized,
    },
    isFetched,
  };
};

export const useQueryGetPageConfigSetAllInvalidate = () => {
  const queryClient = useQueryClient();

  return useCallback(
    (variables?: Parameters<typeof useInfinitePageConfigSetQuery>[2]) =>
      queryClient.invalidateQueries(
        useInfinitePageConfigSetQuery.getKey(variables),
      ),
    [queryClient],
  );
};

type UpdateCallback = (
  draft:
    | WritableDraft<UseInfiniteQueryResult<IPageConfigSetQuery>["data"]>
    | undefined,
) => void;

export const useQueryPageConfigSetAllUpdater = (
  variables?: IPageConfigSetQueryVariables,
) => {
  const queryClient = useQueryClient();

  return useCallback(
    (callback: UpdateCallback) =>
      queryClient.setQueriesData<
        UseInfiniteQueryResult<IPageConfigSetQuery>["data"] | undefined
      >(useInfinitePageConfigSetQuery.getKey(variables), (data) =>
        produce(data, callback),
      ),
    [queryClient, variables],
  );
};
